import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from '../../../store/store';
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../types/button-type';

const POLL_INTERVAL_MS = 5000;

type ComponentProps = ReturnType<typeof mapDispatch>;

const ReconciliationBanner: React.FC<ComponentProps> = ({
  getSetupReconciliationStatus,
  stopSetupReconciliation,
}) => {
  const { id } = useParams();
  const [nextCheckAt, setNextCheckAt] = useState<number | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isStopping, setIsStopping] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastMismatches, setLastMismatches] = useState<string[] | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts, setMaxAttempts] = useState(0);
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const status = await getSetupReconciliationStatus(id);
      if (cancelled) return;
      setNextCheckAt(status?.active && status.nextCheckAt ? new Date(status.nextCheckAt).getTime() : null);
      setLastError(status?.active ? status.lastError ?? null : null);
      // Refreshed by the backend every time a recheck cycle completes (i.e.
      // every time this banner's countdown hits 0), so polling status here
      // is enough to pick up the new list without any extra wiring.
      setLastMismatches(status?.active ? status.lastMismatches ?? null : null);
      setAttempts(status?.attempts ?? 0);
      setMaxAttempts(status?.maxAttempts ?? 0);
      setGaveUp(!!status?.gaveUp);
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [id]);

  useEffect(() => {
    if (nextCheckAt === null) {
      setSecondsRemaining(0);
      return;
    }

    const tick = () => setSecondsRemaining(Math.max(0, Math.round((nextCheckAt - Date.now()) / 1000)));
    tick();
    const intervalId = setInterval(tick, 1000);

    return () => clearInterval(intervalId);
  }, [nextCheckAt]);

  if (nextCheckAt === null) return null;

  const onStopHandler = async () => {
    setIsStopping(true);
    const succeeded = await stopSetupReconciliation(id);
    setIsStopping(false);

    if (succeeded) {
      setNextCheckAt(null);
    }
  };

  return (
    <div
      className={`flex flex-wrap items-start justify-between gap-3 rounded-sm border px-4 py-3 text-sm text-black dark:text-white ${
        gaveUp ? 'border-danger bg-danger/10' : 'border-warning bg-warning/10'
      }`}
    >
      <div className="min-w-0 flex-1">
        {gaveUp ? (
          <span className="block font-medium text-danger">
            Gave up after {maxAttempts} attempts - these settings still don&apos;t match the controller setup.
          </span>
        ) : (
          <span>
            The system is busy updating all the controller settings. Re-checking in {secondsRemaining}s.
            {!!maxAttempts && (
              <span className="text-slate-gray dark:text-gray-400">
                {' '}
                (attempt {attempts} of {maxAttempts})
              </span>
            )}
          </span>
        )}
        {lastError && (
          <span className="mt-1 block text-danger">Last attempt failed: {lastError}</span>
        )}
        {!!lastMismatches?.length && (
          <div className="mt-2">
            <p className="font-medium">
              Settings that don&apos;t match the controller setup ({lastMismatches.length}):
            </p>
            <ul className="mt-1 max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5">
              {lastMismatches.map((mismatch, idx) => (
                <li key={idx}>{mismatch}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <InteractiveButton
        text={isStopping ? 'Stopping...' : gaveUp ? 'Dismiss' : 'Stop'}
        type={ButtonType.PrimaryInverse}
        onClick={onStopHandler}
        isLoading={isStopping}
      />
    </div>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  getSetupReconciliationStatus: dispatch.controllers.getSetupReconciliationStatus,
  stopSetupReconciliation: dispatch.controllers.stopSetupReconciliation,
});

export default connect(null, mapDispatch)(ReconciliationBanner);
